import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Archive,
  ArrowLeft,
  BarChart3,
  Boxes,
  ChevronLeft,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  MoveHorizontal,
  Search,
  Settings,
  Truck,
  Warehouse,
} from "lucide-react";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import api from "@/api";
import { useFormArrowNavigation } from "@/hooks/useFormArrowNavigation";

const navigation = [
  { to: "/dashboard", label: "Vue d’ensemble", icon: LayoutDashboard },
  { to: "/stock-disponible", label: "Stock Disponible", icon: Search },
  { to: "/stock-mouvements", label: "Stock & Mouvements", icon: Boxes },
  { to: "/rapports-stock", label: "Rapports & PDF", icon: BarChart3, requiresReports: true },
  { to: "/livraisons", label: "Livraisons", icon: Truck, requiresDelivery: true },
  { to: "/produit-catalogue", label: "Produit & Catalogue", icon: Archive },
  { to: "/depots", label: "Dépôts & zones", icon: Warehouse },
  { to: "/documents-depot", label: "Documents de dépôt", icon: FileText },
  { to: "/fichiers", label: "Fichiers", icon: Archive },
  { to: "/profil", label: "Mon profil", icon: Settings },
  { to: "/parametres", label: "Paramètres", icon: Settings },
  { to: "/personnel", label: "Personnel", icon: Archive },
];

export default function AppShell({ user, onLogout, children }) {
  useFormArrowNavigation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const canManageDelivery = user.permissions?.includes("*")
    || user.permissions?.includes("delivery:manage");
  const canViewReports = user.permissions?.includes("*")
    || user.permissions?.includes("report:view");
  const profile = user.profile || {};
  const sessionParts = [profile.first_name, profile.last_name]
    .map((value) => value?.trim())
    .filter(Boolean);
  const sessionName = sessionParts.join(" ") || user.name.trim() || "Utilisateur";
  const sessionInitials = sessionName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  const signOut = async () => {
    await api.post("/auth/logout");
    onLogout();
    navigate("/connexion");
  };

  const goBack = () => {
    if (window.history.state?.idx > 0) {
      navigate(-1);
      return;
    }
    navigate("/dashboard");
  };

  return (
    <div className="app-frame" data-testid="application-shell">
      <aside className={`sidebar ${collapsed ? "sidebar-collapsed" : ""} ${mobileOpen ? "sidebar-open" : ""}`} data-testid="primary-navigation">
        <div className="brand-lockup">
          <div className="brand-mark" data-testid="brand-mark"><img src="/api/branding/logo" alt="Logo Ecom Guinée" onError={(event) => { event.currentTarget.style.display = "none"; }} /><span>E.G</span></div>
          {!collapsed && <span data-testid="brand-name">Ecom Guinée</span>}
          <button className="sidebar-toggle" onClick={() => setCollapsed(!collapsed)} data-testid="sidebar-collapse-button" title="Réduire le menu">
            <ChevronLeft size={17} className={collapsed ? "rotate-180" : ""} />
          </button>
        </div>
        <nav className="nav-list" data-testid="main-navigation-links">
          {navigation.filter((item) => (
            (!item.requiresDelivery || canManageDelivery)
            && (!item.requiresReports || canViewReports)
          )).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} onClick={() => setMobileOpen(false)} data-testid={`nav-${item.label.toLowerCase().replaceAll(" ", "-").replaceAll("&", "et")}`} className={({ isActive }) => `nav-link ${isActive ? "nav-link-active" : ""}`}>
                <Icon size={18} />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>
        <div className="sidebar-user" data-testid="sidebar-user-summary">
          <div className="avatar-initials" data-testid="current-user-avatar">
            {user.picture
              ? <img alt="Photo de profil" src={user.picture} data-testid="current-user-profile-photo" />
              : sessionInitials}
          </div>
          {!collapsed && <div className="min-w-0"><p data-testid="current-user-name">{sessionName}</p><span data-testid="current-user-role">{user.role.replaceAll("_", " ")}</span></div>}
          {!collapsed && <button onClick={signOut} className="logout-icon" data-testid="logout-button" title="Se déconnecter"><LogOut size={16} /></button>}
        </div>
      </aside>
      <main className="main-content">
        <header className="topbar">
          <button className="mobile-menu-button" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-navigation-button" title="Ouvrir le menu"><Menu size={20} /></button>
          <button className="app-back-button" data-testid="global-back-button" onClick={goBack} type="button">
            <ArrowLeft size={17} />
            <span>Retour</span>
          </button>
          <button className="topbar-session-user" data-testid="active-session-user" onClick={() => navigate("/profil")} type="button">
            <div className="topbar-session-avatar" data-testid="active-session-photo">
              {user.picture
                ? <img alt={`Photo de ${sessionName}`} src={user.picture} data-testid="active-session-photo-image" />
                : sessionInitials}
            </div>
            <div>
              <strong data-testid="active-session-name">{sessionName}</strong>
              <span data-testid="active-session-label">Session active</span>
            </div>
          </button>
          <div className="topbar-status" data-testid="app-status"><span className="status-dot" /> Système opérationnel</div>
        </header>
        <div className="page-wrap">{children}</div>
      </main>
      <PwaInstallPrompt />
    </div>
  );
}